/**
 * Phase 1.5 WU-4 (R-127) — smart-skip iterator
 *
 * Canonical "visit set" for the verification station's keying flow. The
 * operational model (per R-127): the keyer only ever visits fields below
 * the effective per-field confidence floor, in form order, full stop.
 * Fields with no extracted value (`confidence` null or 0) qualify — they
 * always need keying.
 *
 * This module is a PURE FUNCTION + a thin React hook wrapper. Both the
 * "needs attention" counter (R-135, fixes the bug where the count showed
 * only the 16 OCR-extracted subset instead of the full 35-field visit
 * set) and any test/UI that needs to know "which fields would smart-skip
 * stop on?" consume `computeSmartSkipVisitSet` directly.
 *
 * The store's `advanceField` action still owns the live cursor walk
 * (with isDirty / validation / always-verify edge cases); this module is
 * the simpler "static" set used for counters and orientation. They share
 * the same effective-floor predicate via `@capture/shared`'s
 * `effectiveFloor` helper so the counter and the iteration agree by
 * construction.
 */

import { useMemo } from "react";
import { effectiveFloor } from "@capture/shared";

/**
 * Minimal field shape consumed by the iterator. Matches a strict subset of
 * the verification store's FieldState so test fixtures stay tiny and the
 * helper can be reused from anywhere a `{ name, dataType, ocrConfidence }`
 * tuple exists.
 */
export interface SmartSkipField {
  name: string;
  dataType: string;
  /** Per-field override; null/undefined → fall through to typeFloors. */
  verifyThreshold?: number | null;
  /** OCR-returned confidence; null means "no extracted value at all". */
  ocrConfidence: number | null;
  /**
   * R-239 P4 — re-verify valve forces this field into the visit set
   * regardless of confidence. When true, the field is presented to the
   * operator even if its ocrConfidence is above the effective floor.
   * Computed server-side in GET /api/documents/:id from the job type's
   * reverifyValve + FieldLearningMetric.readsSinceForced.
   */
  forceReverify?: boolean;
  /**
   * F-046 — upstream human verification record (DocumentField.isVerified).
   * Consulted only under the "only_unverified" step policy; absent/false
   * fields behave exactly as before.
   */
  isVerified?: boolean;
  /** F-046 — current validation status; "fail" always flags under any policy. */
  validationStatus?: string;
  /** F-046 — required-empty always flags under any policy. */
  required?: boolean;
  value?: string;
}

/**
 * F-046 — per-step re-verification policy (PipelineStep.reverifyPolicy,
 * shipped as `stepReverifyPolicy` on GET /api/documents/:id).
 * - "standard": today's confidence-driven visit set (the default).
 * - "only_failures": only failing rules + empty required fields visit.
 * - "only_unverified": standard, minus fields already human-verified
 *   upstream (unless failing / required-empty).
 * The R-239 valve (forceReverify) beats the policy in all modes.
 */
export type StepReverifyPolicy = "standard" | "only_failures" | "only_unverified";

export interface SmartSkipVisitSet {
  /** Names of fields the iterator would stop on, in form order. */
  lowConfFieldNames: string[];
  /** Alias for `lowConfFieldNames` — the "visit order" the cursor walks. */
  visitOrder: string[];
  /** Predicate — does the iterator stop on this field name? */
  isLowConf: (name: string) => boolean;
}

/**
 * Pure helper — given the document's fields in form order + per-type
 * floors, returns the smart-skip visit set.
 *
 * Predicate: `(confidence ?? 0) < effectiveFloor(field, typeFloors)`.
 * - Fields with `ocrConfidence === null` are treated as 0 (always-key).
 * - Fields whose `verifyThreshold` is 1.00 always qualify (no OCR
 *   confidence can be `>= 1.00`).
 * - High-conf fields above the effective floor are excluded.
 *
 * The `fields` argument is assumed to be in form order (the caller — the
 * verification store — sorts by displayOrder when loading the document).
 */
export function computeSmartSkipVisitSet(
  fields: readonly SmartSkipField[],
  typeFloors: Record<string, number | null | undefined>,
  policy: StepReverifyPolicy = "standard",
): SmartSkipVisitSet {
  const lowConfFieldNames: string[] = [];
  for (const f of fields) {
    // R-239 P4 — forceReverify is a hard override: forced fields are always
    // visited regardless of confidence OR policy (it's a compliance sampling
    // valve). Check before the threshold compare so a high-confidence forced
    // field is never short-circuited out.
    if (f.forceReverify === true) {
      lowConfFieldNames.push(f.name);
      continue;
    }
    // F-046 — policy-narrowed visit sets. "standard" stays byte-identical to
    // the pre-F-046 confidence-only predicate below (nothing silently
    // changes); the narrowed policies still ALWAYS flag failures and empty
    // required fields.
    if (policy !== "standard") {
      const failing = f.validationStatus === "fail";
      const requiredEmpty = f.required === true && !f.value;
      if (failing || requiredEmpty) {
        lowConfFieldNames.push(f.name);
        continue;
      }
      if (policy === "only_failures") continue;
      // only_unverified — a clean field already human-verified upstream is
      // resolved; the confidence predicate never re-flags it.
      if (f.isVerified === true) continue;
    }
    const floor = effectiveFloor(
      { verifyThreshold: f.verifyThreshold ?? null, dataType: f.dataType },
      typeFloors,
    );
    const conf = f.ocrConfidence ?? 0;
    if (conf < floor) lowConfFieldNames.push(f.name);
  }
  const set = new Set(lowConfFieldNames);
  return {
    lowConfFieldNames,
    visitOrder: lowConfFieldNames,
    isLowConf: (name: string) => set.has(name),
  };
}

/**
 * React hook wrapper around `computeSmartSkipVisitSet`. Memoizes on the
 * fields array + typeFloors object identity — callers should pass stable
 * references (selectors with shallow equality, or the store's slice).
 */
export function useSmartSkipIterator(
  fields: readonly SmartSkipField[],
  typeFloors: Record<string, number | null | undefined>,
  policy: StepReverifyPolicy = "standard",
): SmartSkipVisitSet {
  return useMemo(
    () => computeSmartSkipVisitSet(fields, typeFloors, policy),
    [fields, typeFloors, policy],
  );
}

/**
 * F-088 — index of the field initial focus should land on when a document
 * loads: the FIRST field of the smart-skip visit set (the same per-field
 * verifyThreshold + JT-threshold floors the advancement walk uses, via
 * `computeSmartSkipVisitSet`). An empty visit set keeps index 0 focused —
 * the caller must NOT auto-submit in that case.
 */
export function computeInitialFieldIndex(
  fields: readonly SmartSkipField[],
  jobTypeVerifyThreshold: number | null,
  policy: StepReverifyPolicy = "standard",
): number {
  const { isLowConf } = computeSmartSkipVisitSet(
    fields,
    jobTypeThresholdAsTypeFloors(fields, jobTypeVerifyThreshold),
    policy,
  );
  const idx = fields.findIndex((f) => isLowConf(f.name));
  return idx >= 0 ? idx : 0;
}

/**
 * Adapter — today the verification store carries a single per-JobType
 * `verifyThreshold` (not a per-data-type map). This helper synthesizes
 * the per-data-type map the iterator expects by spreading the JT-level
 * threshold across every dataType present in the document. When the JT
 * threshold is null, returns `{}` so `effectiveFloor` falls through to
 * the platform default (0.95).
 *
 * Once per-data-type floors land in the store (R-126 follow-on or the
 * Calibration tab's `autoSkipFloors` actually plumbs through), callers
 * can pass the real map directly and this adapter goes away.
 */
export function jobTypeThresholdAsTypeFloors(
  fields: readonly SmartSkipField[],
  jobTypeVerifyThreshold: number | null,
): Record<string, number> {
  if (jobTypeVerifyThreshold == null) return {};
  const out: Record<string, number> = {};
  for (const f of fields) out[f.dataType] = jobTypeVerifyThreshold;
  return out;
}
